import React, { useState } from "react";
import useAuthStore from "@/store/useAuthStore";
import { useNavigate } from "react-router-dom";
import { capitalizeFirstLetter } from "@/utils/helper";
import { Eye, Trash2 } from "lucide-react";
import {
  Switch,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  Button,
  Box,
} from "@mui/material";
import OrganizationDetailsDialog from "../OrganizationDetails";
import { useTenantById } from "@/hooks/useTenantDashboardService";
import { useUpdateActiveTenantsById } from "@/hooks/useAdminDashboardService";
import { formatDateUS } from "@/utils/dateUtils";
import { useDeleteTenant } from "@/hooks/useTenantDashboardService";

interface LicenseData {
  id: string;
  contactEmail: string;
  tenant: string;
  assigned: number;
  used: number;
  expiry: string;
  type: string;
  hasStripePlan?: boolean;
  createdAt?: string;
  accountType?: string;
  token: number;
  status?: string;
}

interface LicenseTableProps {
  data: LicenseData[];
  showHeading?: boolean;
  setShouldFetch: () => void;
}

const getInitials = (name: string) => {
  if (!name) return "?";
  const parts = name.trim().split(" ");
  return parts.length > 1
    ? `${parts[0][0]}${parts[1][0]}`.toUpperCase()
    : name.slice(0, 2).toUpperCase();
};

const TenantLicenseTable: React.FC<LicenseTableProps> = ({
  data,
  showHeading = true,
  setShouldFetch,
}) => {
  const { role } = useAuthStore();
  const navigate = useNavigate();
  const [selectedTenantId, setSelectedTenantId] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const { mutate } = useUpdateActiveTenantsById();
  const { mutate: deleteTenant } = useDeleteTenant();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [tenantIdToDelete, setTenantIdToDelete] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleStatusToggle = (id: string, status: boolean) => {
    mutate(
      { payload: { status: !status }, id },
      {
        onSuccess: () => setShouldFetch(),
        onError: (error) => console.error("Toggle Failed:", error),
      },
    );
  };

  const handleConfirmDelete = () => {
    if (!tenantIdToDelete) return;
    setIsDeleting(true);
    deleteTenant(tenantIdToDelete, {
      onSuccess: () => {
        setIsDeleting(false);
        setShowDeleteDialog(false);
        setTenantIdToDelete(null);
        setShouldFetch();
      },
      onError: (error) => {
        console.error("Delete Tenant Error:", error);
        setIsDeleting(false);
      },
    });
  };

  const handleCancelDelete = () => {
    setShowDeleteDialog(false);
    setTenantIdToDelete(null);
  };

  const handleDelete = (id: string) => {
    setTenantIdToDelete(id);
    setShowDeleteDialog(true);
  };

  const [selectedRowData, setSelectedRowData] = useState<LicenseData | null>(null);

  const handleViewClick = (id: string, row: LicenseData) => {
    setSelectedTenantId(id);
    setSelectedRowData(row);
    setDialogOpen(true);
  };

  const { data: tenantDetails } = useTenantById({
    tenantId: selectedTenantId ?? "",
    query: { include: "tenantProfile,usage_log,planMaster,subscriptions" },
  });

  return (
    <div className="bg-surface rounded-2xl shadow-[0_1px_3px_rgba(0,0,0,0.08),0_8px_24px_rgba(0,0,0,0.05)] overflow-hidden">
      <div className="flex items-center justify-between px-6 py-5" style={{ backgroundColor: 'var(--color-header)' }}>
        {showHeading && role === "ADMIN" ? (
          <h2 className="text-xl font-semibold text-foreground">Users</h2>
        ) : (
          <>
            <h2 className="text-xl font-semibold text-foreground ">
              Recent Organizations
            </h2>
            <button
              className="text-accent text-sm font-medium hover:underline cursor-pointer"
              onClick={() => navigate("/tenants")}
            >
              See all →
            </button>
          </>
        )}
      </div>

      {/* Table */}
      <div className="overflow-x-auto  " style={{ backgroundColor: 'var(--color-header)' }}>
        <table className="w-full text-sm text-left border-collapse bg-surface">
          <thead>
            <tr style={{ backgroundColor: 'var(--color-table-header)' }}>
              <th className="py-3 px-6 text-xs font-semibold text-muted uppercase tracking-wider">
                Organization
              </th>
              <th className="py-3 px-4 text-xs font-semibold text-muted uppercase tracking-wider">
                Email
              </th>
              <th className="py-3 px-4 text-xs font-semibold text-muted uppercase tracking-wider text-center">Account Type</th>
              <th className="py-3 px-4 text-xs font-semibold text-muted uppercase tracking-wider text-center">Created At</th>
              <th className="py-3 px-4 text-xs font-semibold text-muted uppercase tracking-wider">
                Expiry
              </th>
              <th className="py-3 px-4 text-xs font-semibold text-muted uppercase tracking-wider text-center">
                Status
              </th>
              <th className="py-3 px-4 text-xs font-semibold text-muted uppercase tracking-wider text-center">
                View
              </th>
              <th className="py-3 px-4 text-xs font-semibold text-muted uppercase tracking-wider text-center">
                Delete
              </th>
            </tr>
          </thead>
          <tbody>
            {data.map((row, idx) => (
              <tr
                key={idx}
                className="text-foreground hover:bg-background/60 transition-colors duration-150"
                style={{
                  borderTop: "1px solid var(--color-divider)",
                }}
              >
                <td className="py-3.5 px-6">
                  <div className="flex items-center gap-3 max-w-[200px]">
                    <div className="w-8 h-8 shrink-0 rounded-full bg-accent/15 text-accent font-semibold text-xs flex items-center justify-center"  style={{
                      backgroundColor:
                        "color-mix(in srgb, var(--color-stat-accent) 15%, transparent)",
                      color: "var(--color-stat-accent)",
                    }}>
                      {getInitials(row.tenant)}
                    </div>
                    <Tooltip title={row.tenant} arrow placement="top">
                      <span className="truncate font-medium">{row.tenant}</span>
                    </Tooltip>
                  </div>
                </td>

                <td className="py-3.5 px-4 max-w-[180px]">
                  <Tooltip title={row.contactEmail} arrow placement="top">
                    <span className="block truncate text-muted">
                      {row.contactEmail}
                    </span>
                  </Tooltip>
                </td>

                <td className="py-3.5 px-4 text-center">
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold" style={{ backgroundColor: "color-mix(in srgb, var(--color-stat-accent) 15%, transparent)", color: "var(--color-stat-accent)" }}>
                    {capitalizeFirstLetter(row.accountType || "Company")}
                  </span>
                </td>

                <td className="py-3.5 px-4 text-center text-muted">
                  {row.createdAt ? formatDateUS(row.createdAt) : "N/A"}
                </td>

                <td className="py-3.5 px-4 text-muted">
                  {row.expiry ? (
                    <span className="font-medium text-white">{formatDateUS(row.expiry)}</span>
                  ) : row.hasStripePlan ? (
                    <span className="text-muted">Active</span>
                  ) : (
                    <span className="text-muted">No Plan</span>
                  )}
                </td>

                <td className="py-3.5 px-4 text-center">
                  <div className="flex items-center justify-center gap-2">
                    <Switch
                      size="small"
                      checked={row.status === "active"}
                      onChange={(e) => handleStatusToggle(row.id, e.target.checked)}
                      sx={{
                        "& .MuiSwitch-switchBase.Mui-checked": {
                          color: "var(--color-stat-accent)",
                        },
                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                          backgroundColor: "var(--color-stat-accent)",
                        },
                      }}
                    />
                    <span
                      className={`text-xs font-medium ${
                        row.status === "active" ? "text-accent" : "text-muted"
                      }`}
                    >
                      {row.status === "active" ? "Active" : "Inactive"}
                    </span>
                  </div>
                </td>

                <td className="py-3.5 px-4 text-center">
                  <button
                    className="p-2 rounded-full hover:bg-accent/10 transition-colors"
                    aria-label="View Details"
                    onClick={() => handleViewClick(row.id, row)}
                  >
                    <Eye size={17} className="text-muted hover:text-accent transition-colors" />
                  </button>
                </td>

                <td className="py-3.5 px-4 text-center">
                  <button
                    className="p-2 rounded-full hover:bg-red-500/10 transition-colors"
                    aria-label="Delete Tenant"
                    onClick={() => handleDelete(row.id)}
                  >
                    <Trash2 size={17} className="text-muted hover:text-red-500 transition-colors" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Delete confirmation dialog */}
      <Dialog
        open={showDeleteDialog}
        onClose={handleCancelDelete}
        PaperProps={{
          sx: {
            backgroundColor: "var(--color-surface)",
            color: "var(--color-text)",
            borderRadius: "16px",
            padding: "8px",
            boxShadow: "0 8px 32px rgba(0,0,0,0.16)",
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: "bold", color: "var(--color-stat-accent)" }}>
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Are you sure you want to delete this tenant? This action cannot be undone.
          </Typography>
          <Box display="flex" justifyContent="flex-end" gap={2}>
            {isDeleting ? (
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                <span>Deleting...</span>
              </div>
            ) : (
              <>
                <Button
                  variant="outlined"
                  onClick={handleCancelDelete}
                  sx={{
                    borderRadius: "10px",
                    color: "var(--color-stat-accent)",
                    borderColor: "var(--color-stat-accent)",
                    "&:hover": { backgroundColor: "var(--color-background)" },
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleConfirmDelete}
                  sx={{
                    borderRadius: "10px",
                    backgroundColor: "var(--color-stat-accent)",
                    color: "black",
                    fontWeight: "bold",
                    "&:hover": { opacity: 0.9 },
                  }}
                >
                  Delete
                </Button>
              </>
            )}
          </Box>
        </DialogContent>
      </Dialog>

      {dialogOpen && tenantDetails && (
        <OrganizationDetailsDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          data={{
            ...tenantDetails,
            expiryDate:
              (tenantDetails as any)?.subscriptions?.find((s: any) => s.status === "ACTIVE")?.endDate ||
              (tenantDetails as any)?.subscriptions?.[0]?.endDate ||
              selectedRowData?.expiry,
            tenantProfile: tenantDetails.tenantProfile
              ? {
                  id: tenantDetails.tenantProfile.id ?? "",
                  tenantId: tenantDetails.tenantProfile.id ?? "",
                  about: tenantDetails.tenantProfile.about ?? "",
                  competitors: tenantDetails.tenantProfile.competitors ?? [],
                  partners: tenantDetails.tenantProfile.partners ?? [],
                  productsServices:
                    tenantDetails.tenantProfile.productsServices ?? [],
                  salesMethodology:
                    tenantDetails.tenantProfile.salesMethodology ?? [],
                  targetIndustries:
                    tenantDetails.tenantProfile.targetIndustries ?? [],
                  uniqueSellingPoints:
                    tenantDetails.tenantProfile.uniqueSellingPoints ?? [],
                  valueProposition: Array.isArray(
                    tenantDetails.tenantProfile.valueProposition,
                  )
                    ? tenantDetails.tenantProfile.valueProposition
                    : tenantDetails.tenantProfile.valueProposition
                      ? [tenantDetails.tenantProfile.valueProposition]
                      : [],
                  createdAt: tenantDetails.tenantProfile.createdAt ?? "",
                  updatedAt: tenantDetails.tenantProfile.updatedAt ?? "",
                  updatedby: tenantDetails.tenantProfile.updatedby ?? "",
                }
              : undefined,
            planMaster: tenantDetails.planMaster
              ? {
                  ...tenantDetails.planMaster,
                  planMasterId: tenantDetails.planMaster.id,
                }
              : undefined,
          }}
        />
      )}
    </div>
  );
};

export default TenantLicenseTable;